import React, { useState } from "react";
import classNames from "classnames";
import { Popover, PopoverContent } from "@design-system/Popover/Popover";
import styles from "./SlateEditor.module.scss";

interface SlateListPopoverProps<TItem> {
    isOpen: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLSpanElement | null>;
    items: TItem[];
    getItemKey: (item: TItem) => string;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    onSelect: (item: TItem) => void;
    renderItem: (item: TItem) => React.ReactNode;
    emptyState?: React.ReactNode;
}

export function SlateListPopover<TItem>({
    isOpen,
    onClose,
    anchorRef,
    items,
    getItemKey,
    activeIndex,
    setActiveIndex,
    onSelect,
    renderItem,
    emptyState,
}: SlateListPopoverProps<TItem>) {
    // Hosts reset their query as they close; the exit fade keeps the last open list.
    const [shownList, setShownList] = useState({ items, activeIndex });
    if (
        isOpen &&
        (shownList.items !== items || shownList.activeIndex !== activeIndex)
    ) {
        setShownList({ items, activeIndex });
    }
    const visibleItems = isOpen ? items : shownList.items;
    const visibleActiveIndex = isOpen ? activeIndex : shownList.activeIndex;

    return (
        <Popover
            open={isOpen}
            onOpenChange={(next) => {
                if (!next) {
                    onClose();
                }
            }}
        >
            <PopoverContent
                align="start"
                side="top"
                sideOffset={4}
                anchor={anchorRef}
                initialFocus={false}
                className={styles.mentionPopover}
            >
                <div className={styles.mentionPopoverBody} role="listbox">
                    {visibleItems.length === 0 && emptyState}
                    {visibleItems.map((item, index) => (
                        <div
                            key={getItemKey(item)}
                            role="option"
                            aria-selected={index === visibleActiveIndex}
                            className={classNames(styles.slashCommandOption, {
                                [styles.slashCommandOptionSelected]:
                                    index === visibleActiveIndex,
                            })}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                onSelect(item);
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            {renderItem(item)}
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
